import React, { FormEvent, useState } from "react";
import axios from "axios";
import { Alert, Button, Form, Modal, Spinner } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { toast } from "react-toastify";
import { useTranslate } from "../hooks/LanguageProvider";
import { useTheme } from "../hooks/ThemeContext";
import "../assets/css/settings.css";

const backendUrl = process.env.REACT_APP_BACKEND_URL || "http://localhost:5000";
type LanguageKey = "en" | "fr" | "ar" | "es";

const initialForm = {
  newPassword: "",
  confirmPassword: "",
};

const PASSWORD_MIN_LENGTH = 8;

export function Settings() {
  const navigate = useNavigate();
  const { logout } = useAuth();
  const [deletionOpen, setDeletionOpen] = useState(false);
  const [deletionPassword, setDeletionPassword] = useState("");
  const [deletionConfirmed, setDeletionConfirmed] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [deletionError, setDeletionError] = useState("");
  const { translate, lang, setLang } = useTranslate();
  const { isDarkMode, toggleTheme } = useTheme();
  const [form, setForm] = useState(initialForm);
  const [showPasswords, setShowPasswords] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  const languages: Array<{
    key: LanguageKey;
    label: string;
    flag: string;
  }> = [
    { key: "fr", label: "Francais", flag: "fr" },
    { key: "en", label: "English", flag: "en" },
    { key: "ar", label: "Arabic", flag: "ar" },
    { key: "es", label: "Spanish", flag: "es" },
  ];

  const isPasswordValid =
    form.newPassword.trim().length >= PASSWORD_MIN_LENGTH;
  const isPasswordTooShort =
    form.newPassword.length > 0 && !isPasswordValid;

  const passwordsDoNotMatch =
    form.confirmPassword.length > 0 &&
    form.newPassword !== form.confirmPassword;

  const canSubmit =
    form.newPassword.length > 0 &&
    form.confirmPassword.length > 0 &&
    isPasswordValid &&
    !passwordsDoNotMatch &&
    !isSubmitting;

  const handleChange =
    (field: keyof typeof initialForm) =>
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setErrorMessage("");
      setForm((current) => ({
        ...current,
        [field]: event.target.value,
      }));
    };

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (!canSubmit) return;

    setIsSubmitting(true);
    setErrorMessage("");

    try {
      const response = await axios.post(
        `${backendUrl}/api/users/change-password`,
        form,
        { withCredentials: true },
      );

      const message =
        response.data?.message || translate("Mot de passe modifie avec succes.");
      toast.success(message, { position: "top-right" });
      setForm(initialForm);
    } catch (error: any) {
      const message =
        error?.response?.data?.message ||
        translate("Impossible de modifier le mot de passe.");
      setErrorMessage(message);
      toast.error(message, { position: "top-right" });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleLanguageChange = (nextLang: LanguageKey) => {
    setLang(nextLang);
    toast.success(translate("Language changed successfully"), {
      position: "top-right",
    });
  };

  const closeDeletion = () => {
    if (deleting) return;
    setDeletionOpen(false);
    setDeletionPassword("");
    setDeletionConfirmed(false);
    setDeletionError("");
  };

  const handleDeletion = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (deleting || !deletionPassword || !deletionConfirmed) return;
    setDeleting(true);
    setDeletionError("");
    try {
      const response = await axios.delete(`${backendUrl}/api/account`, {
        withCredentials: true,
        data: { password: deletionPassword, confirmation: "DELETE" },
      });
      if (response.data?.deleted !== true) throw new Error("Deletion not confirmed");
      setDeletionPassword("");
      await logout();
      toast.success(translate("Votre compte a été supprimé."));
      navigate("/login", { replace: true });
    } catch (error: any) {
      setDeletionError(error?.response?.data?.message || translate("Impossible de confirmer la suppression. Vérifiez votre connexion avant de réessayer."));
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="settings-page">
      <div className="settings-shell">
        <div className="settings-header">
          <div className="settings-header-icon">
            <i className="las la-cog"></i>
          </div>
          <div>
            <h3>{translate("Parametres du compte")}</h3>
            <p>
              {translate("Change password")}, {translate("Language")} &{" "}
              {translate("Theme")}
            </p>
          </div>
        </div>

        <div className="settings-dashboard-grid">
          <section className="settings-card settings-password-card">
            <div className="settings-card-heading">
              <div className="settings-icon settings-icon-yellow">
                <i className="las la-shield-alt"></i>
              </div>
              <div>
                <h4>{translate("Change password")}</h4>
                <p>
                  {translate(
                    "Un email de notification sera envoye apres la modification du mot de passe.",
                  )}
                </p>
              </div>
            </div>

            {errorMessage && (
              <Alert variant="danger" className="py-2">
                {errorMessage}
              </Alert>
            )}

            <Form onSubmit={handleSubmit}>
              <div className="settings-form-grid">
                <Form.Group controlId="newPassword">
                  <Form.Label>{translate("Nouveau mot de passe")}</Form.Label>
                  <div className="settings-input-wrap">
                    <Form.Control
                      type={showPasswords ? "text" : "password"}
                      value={form.newPassword}
                      onChange={handleChange("newPassword")}
                      autoComplete="new-password"
                      isInvalid={isPasswordTooShort}
                    />
                    <i className="las la-key"></i>
                  </div>
                </Form.Group>

                <Form.Group controlId="confirmPassword">
                  <Form.Label>{translate("Confirmer le mot de passe")}</Form.Label>
                  <div className="settings-input-wrap">
                    <Form.Control
                      type={showPasswords ? "text" : "password"}
                      value={form.confirmPassword}
                      onChange={handleChange("confirmPassword")}
                      autoComplete="new-password"
                      isInvalid={passwordsDoNotMatch}
                    />
                    <i className="las la-lock"></i>
                  </div>
                </Form.Group>
              </div>

              {(isPasswordTooShort || passwordsDoNotMatch) && (
                <div className="text-danger small mt-2">
                  {passwordsDoNotMatch
                    ? translate("Les mots de passe ne correspondent pas")
                    : translate("Le mot de passe doit contenir au moins 8 caracteres.")}
                </div>
              )}

              <div className="settings-password-hint">
                <i className="las la-info-circle"></i>
                <span>
                  {translate("Le mot de passe doit contenir au moins 8 caracteres.")}
                </span>
              </div>

              <div className="settings-actions">
                <Form.Check
                  type="checkbox"
                  id="show-passwords"
                  checked={showPasswords}
                  onChange={(event) => setShowPasswords(event.target.checked)}
                  label={translate("Afficher les mots de passe")}
                />

                <Button
                  type="submit"
                  disabled={!canSubmit}
                  className="settings-primary-btn"
                >
                  {isSubmitting ? (
                    <Spinner animation="border" size="sm" className="me-2" />
                  ) : (
                    <i className="las la-save me-2"></i>
                  )}
                  {translate("Save changes")}
                </Button>
              </div>
            </Form>
          </section>

          <aside className="settings-side-stack">
            <section className="settings-card">
              <div className="settings-card-heading settings-card-heading-small">
                <div>
                  <h4>{translate("Language")}</h4>
                  <p>{translate("Choisir la langue d'affichage")}</p>
                </div>
                <div className="settings-icon settings-icon-blue">
                  <i className="las la-language"></i>
                </div>
              </div>

              <div className="settings-language-grid">
                {languages.map((language) => {
                  const active = lang === language.key;

                  return (
                    <button
                      key={language.key}
                      type="button"
                      onClick={() => handleLanguageChange(language.key)}
                      className={`settings-language-btn ${
                        active ? "is-active" : ""
                      }`}
                    >
                      <img
                        src={`asset/images/small/flag-${language.flag}.png`}
                        alt={language.label}
                      />
                      {translate(language.label)}
                    </button>
                  );
                })}
              </div>
            </section>

            <section className="settings-card">
              <div className="settings-card-heading settings-card-heading-small">
                <div>
                  <h4>{translate("Theme")}</h4>
                  <p>{translate("Choisir le mode d'affichage")}</p>
                </div>
                <div className="settings-icon settings-icon-yellow">
                  <i className={`las ${isDarkMode ? "la-moon" : "la-sun"}`}></i>
                </div>
              </div>

              <div className="settings-theme-row">
                <div>
                  <strong>
                    {isDarkMode ? translate("Dark mode") : translate("Light mode")}
                  </strong>
                  <span>
                    {isDarkMode
                      ? translate("Interface sombre activee")
                      : translate("Interface claire activee")}
                  </span>
                </div>
                <Form.Check
                  type="switch"
                  id="settings-theme-switch"
                  checked={isDarkMode}
                  onChange={toggleTheme}
                />
              </div>
            </section>
          </aside>
        </div>
        <section className="settings-card mt-4" id="delete-account">
          <h4 className="text-danger">{translate("Supprimer mon compte")}</h4>
          <p>{translate("La suppression du compte AIDA et des données associées est définitive. Vous perdrez l’accès à votre espace.")}</p>
          <Button variant="outline-danger" onClick={() => setDeletionOpen(true)}>
            {translate("Supprimer mon compte")}
          </Button>
        </section>
        <Modal show={deletionOpen} onHide={closeDeletion} backdrop="static" keyboard={!deleting} centered>
          <Form onSubmit={handleDeletion}>
            <Modal.Header closeButton={!deleting}>
              <Modal.Title>{translate("Confirmer la suppression du compte")}</Modal.Title>
            </Modal.Header>
            <Modal.Body>
              <Alert variant="danger">{translate("Cette action est irréversible. Votre compte et les données associées seront supprimés. Les données partagées appartenant à d’autres comptes ne seront pas supprimées.")}</Alert>
              {deletionError && <Alert variant="danger" role="alert">{deletionError}</Alert>}
              <Form.Group controlId="deletion-password">
                <Form.Label>{translate("Mot de passe actuel")}</Form.Label>
                <Form.Control type="password" autoComplete="current-password" required maxLength={1024} value={deletionPassword} disabled={deleting} onChange={(event) => setDeletionPassword(event.target.value)} />
              </Form.Group>
              <Form.Check className="mt-3" id="confirm-account-deletion" checked={deletionConfirmed} disabled={deleting} onChange={(event) => setDeletionConfirmed(event.target.checked)} label={translate("Je comprends que la suppression est définitive et souhaite supprimer mon compte.")} />
            </Modal.Body>
            <Modal.Footer>
              <Button variant="secondary" disabled={deleting} onClick={closeDeletion}>{translate("Annuler")}</Button>
              <Button variant="danger" type="submit" disabled={deleting || !deletionPassword || !deletionConfirmed}>
                {deleting && <Spinner animation="border" size="sm" className="me-2" />}
                {translate("Supprimer définitivement")}
              </Button>
            </Modal.Footer>
          </Form>
        </Modal>
      </div>
    </div>
  );
}
